"use client"

import React, { useEffect, type ReactNode } from "react"
import { useRouter } from "next/navigation"
import { Download, RotateCw } from "lucide-react"
import { PillButton } from "@/components/blades/blade"
import { BladeStackProvider, useBlades, type BladeRegistry, type BladeRenderProps } from "@/components/blades/blade-stack"
import { HubTabs, activeHubTab, type HubTabDefinition } from "@/components/hub/hub-tabs"
import { PullBlade, type PullBladeInitial } from "@/components/data-pull/pull-blade"
import { useHubCounts } from "@/hooks/use-hub-counts"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export interface HubShellProps {
  view: string | null
  mappingName: string | null
  /** Whether the tool tabs and Data Pull are usable (a source exists) */
  hasSources: boolean
  /** Starts every tool over, discarding what each was holding */
  onRefresh: () => void
  children: ReactNode
}

const PullEntry = ({ entry, depth, trail, close, popTo }: BladeRenderProps<{ initial?: PullBladeInitial; returnTo?: string }>) => {
  const router = useRouter()
  return (
    <PullBlade
      open
      depth={depth}
      trail={trail}
      onTrailClick={popTo}
      initial={entry.params.initial}
      onClose={() => {
        close()
        if (entry.params.returnTo) router.push(entry.params.returnTo)
      }}
    />
  )
}

/** The header's own pull, kept apart from the one the explorer hands its scope to */
export const PULL_BLADE_ID = "pull-global"

const HUB_BLADES: BladeRegistry = {
  pull: (props) => <PullEntry {...(props as BladeRenderProps<{ initial?: PullBladeInitial; returnTo?: string }>)} />,
}

/**
 * The ATS Hub: a header band, summary-card tabs for the tools, and the tool canvas
 * below. Every tool renders inside it, so the user always sees where they are; the
 * hub-level blade stack hosts blades any tool can open (Data Pull), seeded with the
 * browsing scope the active tool publishes. Tools stay mounted behind the tabs, so
 * Refresh is the way to start them over.
 */
export function HubShell(props: HubShellProps) {
  return (
    <BladeStackProvider registry={HUB_BLADES}>
      <HubFrame {...props} />
    </BladeStackProvider>
  )
}

function HubFrame({ view, mappingName, hasSources, onRefresh, children }: HubShellProps) {
  const router = useRouter()
  const blades = useBlades()
  const counts = useHubCounts()
  const { t } = useTranslation()
  const active = activeHubTab(view, mappingName)

  // Started from the header, a pull is its own errand: it carries no scope from the explorer
  // and keeps its own blade, so one pull can be set up while another is mid-configuration.
  const openPull = (returnTo?: string) => blades.open("pull", t("hub.actions.dataPull"), { returnTo }, { id: PULL_BLADE_ID, size: "xl" })

  // The legacy ?view=data-pull deep link opens the pull blade over the explorer
  useEffect(() => {
    if (view === "data-pull") openPull("?view=explorer")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  const select = (tab: HubTabDefinition) => router.push(tab.view ? `?view=${tab.view}` : "?mappingName=selector")

  return (
    <div className="flex h-full min-h-0 flex-col bg-canvas">
      <div className="mx-4 mt-3 flex items-center justify-between rounded-lg bg-band px-5 py-2.5">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-[17px] font-extrabold tracking-tight text-foreground">{t("hub.band.title")}</h1>
          <span className="hidden text-xs text-muted-foreground sm:inline">{t("hub.band.subtitle")}</span>
        </div>
        <div className="flex items-center gap-2">
          <PillButton variant="outline" className="h-8 px-3 py-0 text-xs" onClick={onRefresh} title={t("hub.actions.refreshTitle")}>
            <RotateCw className="h-3.5 w-3.5" /> {t("common.actions.refresh")}
          </PillButton>
          <PillButton variant="outline" className="h-8 px-3 py-0 text-xs" disabled={!hasSources} onClick={() => openPull()}>
            <Download className="h-3.5 w-3.5" /> {t("hub.actions.dataPull")}
          </PillButton>
        </div>
      </div>

      <HubTabs active={active} counts={counts} onSelect={select} />

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>
    </div>
  )
}
